import React, { useState, useCallback, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import GenericSelect from '../../components/ui/GenericSelect';
import DateTimePicker from '../../components/ui/DateTimePicker';
import {
  useDelegationsApi, employeeAsync, DELEGATION_SCOPES, DELEGATION_SCOPE_LABEL_KEYS,
} from './config';

// ═══════ KARAR VEKÂLETİ — İK'NIN KURDUĞU/KAPATTIĞI YÜZEY ═══════
//
// Gövde Faz J'nin `performance-settings/DelegationsTab.js`'inden taşındı (DataTable +
// GenericFormDrawer kabuğu, elle doğrulama, pasifleştirme onayı — hepsi AYNI). İki yerde
// birden kullanılır ve TEK kopya kalır:
//   • Performans Ayarları → Vekâletler sekmesi (`lockedScope="performance"`)
//   • Vekâletler sayfası (platform, kapsam SEÇİLİR)
// İki kopya olsaydı biri er ya da geç kapsam alanını ya da kapıyı kaybederdi.
//
// TARİH ALANLARI DatePicker'dır (mode="date") — düz metin girişi YASAK.
// Form GenericFormDrawer'dadır — modal DEĞİL.
const FieldGroup = styled.div`
  margin-bottom: 16px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const SaveErrorText = styled.p`
  font-size: 12px;
  color: #B02A37;
  margin: 8px 0;
  font-weight: 600;
`;

// "Liste yüklenemedi" ile "hiç vekâlet yok" AYNI ŞEY DEĞİL. api.js GET okumalarında
// reject etmediği için ikisi de boş tabloya düşerdi; İK, birinin yetkisini tam da
// sorguladığı anda "vekâlet yok" diye YANLIŞ bir cevap görürdü.
const LoadErrorBanner = styled.div`
  margin-bottom: 12px;
  padding: 10px 14px;
  border-radius: 8px;
  background: #FEF2F2;
  color: #B02A37;
  font-size: 13px;
  font-weight: 600;
`;

export default function DelegationsPanel({ lockedScope = null }) {
  const { t } = useTranslation();
  // Vekâlet mutasyonları sunucuda aktif enterpriseAdmin ister (403
  // performance.delegation.hrOnly). Buradaki kontrol o kapının arayüz ikizidir:
  // aksiyon kolonu ve "Yeni" butonu, sunucunun kabul etmeyeceği bir eylemi sunmasın.
  // İK rolü ayrımı — İK = sunucunun kararı (AuthContext.isHr), Şirket Yöneticisi DEĞİL.
  const { canWrite, isHr } = useAuth();
  const canManage = canWrite('EM') && Boolean(isHr);

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  const {
    fetching, data, loadFailed, pagination, columns: baseColumns,
    createDelegation, deactivateDelegation,
  } = useDelegationsApi(queryParams, lockedScope);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scope, setScope] = useState(lockedScope || DELEGATION_SCOPES[0]);
  const [delegatorId, setDelegatorId] = useState(null);
  const [delegateId, setDelegateId] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [saveError, setSaveError] = useState('');
  const [deactivateConfirm, setDeactivateConfirm] = useState({ isOpen: false, row: null });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!drawerOpen) return;
    setScope(lockedScope || DELEGATION_SCOPES[0]);
    setDelegatorId(null);
    setDelegateId(null);
    setStartDate(null);
    setEndDate(null);
    setSaveError('');
  }, [drawerOpen, lockedScope]);

  const scopeOptions = useMemo(
    () => DELEGATION_SCOPES.map((s) => ({ id: s, name: t(DELEGATION_SCOPE_LABEL_KEYS[s]) })),
    [t],
  );

  const handleDataStateChange = useCallback((_action, tableState) => {
    setFilterList(tableState.filterList);
    setQueryParams(parseTableParams(tableState, baseColumns));
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => setFilterList(newFilterList), []);

  const openNew = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  // İstemci-ipucu (asıl kapı BE — performance.delegation.{selfDelegation,invalidWindow,
  // invalidScope}): dört alan da zorunlu (schema=[] olduğu için GenericFormDrawer'ın kendi
  // required-alan kontrolü BURADA hiç çalışmaz); aynı kişi hem veren hem vekil olamaz;
  // bitiş başlangıçtan önce olamaz (aynı gün SERBEST — pencere İKİ UÇTAN DA kapsayıcıdır).
  const selfDelegation = !!delegatorId && !!delegateId && delegatorId === delegateId;
  const invalidWindow = !!startDate && !!endDate && endDate < startDate;

  const handleSave = useCallback(async () => {
    if (!scope || !delegatorId || !delegateId || !startDate || !endDate) {
      setSaveError(t('delegations.requiredFieldsHint'));
      return false;
    }
    if (selfDelegation) {
      setSaveError(t('delegations.selfDelegationHint'));
      return false;
    }
    if (invalidWindow) {
      setSaveError(t('delegations.invalidWindowHint'));
      return false;
    }
    setSaveError('');
    return createDelegation({
      scope,
      delegatorEmployeeId: delegatorId,
      delegateEmployeeId: delegateId,
      startDate,
      endDate,
    });
  }, [scope, selfDelegation, invalidWindow, delegatorId, delegateId, startDate, endDate, createDelegation, t]);

  const requestDeactivate = useCallback((row) => setDeactivateConfirm({ isOpen: true, row }), []);

  const confirmDeactivate = useCallback(async () => {
    setBusy(true);
    await deactivateDelegation(deactivateConfirm.row.id);
    setBusy(false);
    setDeactivateConfirm({ isOpen: false, row: null });
  }, [deactivateDelegation, deactivateConfirm.row]);

  const actionOptions = useMemo(() => {
    if (!canManage) return [];
    return [
      {
        type: 'delete',
        label: t('delegations.actions.deactivate'),
        onClick: requestDeactivate,
      },
    ];
  }, [canManage, t, requestDeactivate]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            if (!row) return null;
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  return (
    <div>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        title={t('delegations.new')}
        schema={[]}
        onSave={handleSave}
      >
        {/* Kapsam SABİTSE seçici gösterilmez: sekme kendi yüzeyinin dışına yazmaz ve
            kullanıcıya anlamsız tek seçenekli bir kutu sunulmaz. */}
        {!lockedScope && (
          <FieldGroup>
            <GenericSelect
              label={t('delegations.form.scopeLabel')}
              options={scopeOptions}
              value={scope}
              onChange={setScope}
              required
              // Kapsam BOŞ BIRAKILAMAZ: kapsamsız bir vekâlet hangi kararlara yetki
              // verdiğini söylemez. Temizleme düğmesi kapatılır (GenericSelect'in
              // varsayılanı açık) — kullanıcı boş bir seçimi kaydetmeye çalışamasın.
              clearable={false}
            />
          </FieldGroup>
        )}
        <Row>
          <FieldGroup>
            <AsyncGenericSelect
              label={t('delegations.form.delegatorLabel')}
              value={delegatorId}
              onChange={setDelegatorId}
              onSearch={employeeAsync.onSearch}
              onGetById={employeeAsync.onGetById}
              required
            />
          </FieldGroup>
          <FieldGroup>
            <AsyncGenericSelect
              label={t('delegations.form.delegateLabel')}
              value={delegateId}
              onChange={setDelegateId}
              onSearch={employeeAsync.onSearch}
              onGetById={employeeAsync.onGetById}
              required
            />
          </FieldGroup>
        </Row>
        <Row>
          <FieldGroup>
            <DateTimePicker
              label={t('delegations.form.startDateLabel')}
              mode="date"
              value={startDate}
              onChange={setStartDate}
              required
            />
          </FieldGroup>
          <FieldGroup>
            <DateTimePicker
              label={t('delegations.form.endDateLabel')}
              mode="date"
              value={endDate}
              onChange={setEndDate}
              required
            />
          </FieldGroup>
        </Row>

        {saveError && <SaveErrorText>{saveError}</SaveErrorText>}
      </GenericFormDrawer>

      <ConfirmDialog
        isOpen={deactivateConfirm.isOpen}
        title={t('delegations.deactivateConfirm.title')}
        message={t('delegations.deactivateConfirm.message', {
          delegate: deactivateConfirm.row?.delegateName,
          delegator: deactivateConfirm.row?.delegatorName,
        })}
        onConfirm={confirmDeactivate}
        onCancel={() => setDeactivateConfirm({ isOpen: false, row: null })}
        loading={busy}
      />

      {loadFailed && !fetching && (
        <LoadErrorBanner data-role="delegations-load-error">
          {t('delegations.loadFailed')}
        </LoadErrorBanner>
      )}

      <DataTable
        title={t('delegations.title')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        canCreateNewRecord={canManage}
        onNewButtonClick={openNew}
        filterList={filterList}
        onFilterConfirm={handleFilterConfirm}
        onTableChanged={handleDataStateChange}
      />
    </div>
  );
}
